/**
 * Exercice « sur la feuille » — état d'un QCM joué au clavier du dock :
 * on choisit (une ou plusieurs touches), puis « Check » envoie.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useDelayedReveal } from '@jeraisonne/ui';
import type { ExerciseBlock } from '@/lib/exercise-v2/types';
import { checkpointAnswerMode } from '@/lib/exercise-v2/block-helpers';
import { choiceKeyLayout, toggleChoice, type PaperQcm } from '@jeraisonne/ui/paper';
import { buildQcmOptions } from './qcm-options';

export type { PaperQcm };

export function usePaperQcm({
  checkpoint,
  blocks,
  seed,
  answer,
  hasText,
  pending = false,
  onSubmit,
}: {
  checkpoint: ExerciseBlock;
  blocks: ExerciseBlock[];
  seed: number;
  answer: { chosen_ids: string[] } | undefined;
  hasText: boolean;
  pending?: boolean;
  onSubmit: (chosenIds: string[]) => void | Promise<void>;
}): PaperQcm {
  const options = useMemo(
    () => buildQcmOptions(checkpoint, blocks, seed),
    [checkpoint, blocks, seed],
  );
  const multiple = checkpointAnswerMode(checkpoint) === 'multiple';
  const layout = useMemo(
    () =>
      choiceKeyLayout(
        options.map((o) => ({ id: o.id, label: o.label, hasVisuals: o.visuals.length > 0 })),
      ),
    [options],
  );
  const revealed = !!answer;

  // même délai de lecture que les cartes (2,5 s), pas de délai si déjà répondue
  const shown = useDelayedReveal({ key: checkpoint.id, immediate: revealed || !hasText });

  const [selected, setSelected] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  useEffect(() => {
    setSelected([]);
    setSending(false);
    sendingRef.current = false;
  }, [checkpoint.id]);
  const busy = pending || sending;

  const toggle = (id: string) => {
    if (revealed || busy || !shown) return;
    setSelected((prev) => toggleChoice(prev, id, multiple));
  };

  const check = () => {
    if (revealed || sendingRef.current || selected.length === 0) return;
    sendingRef.current = true;
    setSending(true);
    let result: void | Promise<void>;
    try {
      result = onSubmit(selected);
    } catch {
      result = undefined;
    }
    void Promise.resolve(result)
      .catch(() => undefined)
      .finally(() => {
        sendingRef.current = false;
        setSending(false);
      });
  };

  return {
    options,
    multiple,
    layout,
    selected,
    chosen: answer?.chosen_ids ?? [],
    revealed,
    shown,
    busy,
    toggle,
    check,
  };
}
